import React, { useState, useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, DownloadIcon, ShieldCheckIcon, BookOpenIcon, UsersIcon, ClockIcon, AwardIcon, ChevronDownIcon, MailIcon, PhoneIcon, FileTextIcon, BarChart3Icon, LeafIcon, FlameIcon, CheckIcon, AlertTriangleIcon, BriefcaseIcon, GraduationCapIcon, LayersIcon, LandmarkIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { programs } from './courseData';
import { navigate } from '../utils/navigate';
import { MAILTO } from '../utils/mailto';
/* ─── Fade-in wrapper ─── */
function FadeIn({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} className={className} initial={{
    opacity: 0,
    y: 24
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-1699">

      {children}
    </motion.div>;
}
/* ════════════════════════════════════════
   1. HERO — light bg matching DMRVHero
════════════════════════════════════════ */
function HeroSection() {
  return <section className="relative w-full overflow-hidden bg-white" data-id="element-1700">
      {/* Subtle grid texture */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-1701">

        <defs data-id="element-1702">
          <pattern id="hero-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-1703">

            <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-1704" />

            <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-1705" />

          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hero-grid)" data-id="element-1706" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36" data-id="element-1707">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1708">
          {/* Left: Text */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1709">

            <div className="flex items-center gap-3 mb-4" data-id="element-1710">
              <img src="https://f000.backblazeb2.com/file/jpgshared/fEKflaIh" alt="CarbonSafe" className="h-6 w-auto opacity-60" data-id="element-1711" />

              <span className="text-carbon-deep/30 text-sm" data-id="element-1712">|</span>
              <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright" data-id="element-1713">
                Training & Certification
              </p>
            </div>

            <h1 className="text-[3.5rem] font-bold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-1714">
              ESG &{' '}
              <span className="relative inline-block" data-id="element-1715">
                <span className="relative z-10" data-id="element-1716">Climate Training</span>
                <span className="absolute bottom-1 left-0 right-0 h-[40%] bg-carbon-bright/25 -z-0 rounded-sm" aria-hidden="true" data-id="element-1717" />

              </span>
            </h1>

            <p className="mt-6 text-lg text-carbon-deep leading-relaxed max-w-xl" data-id="element-1718">
              Prepare your institution for IFRS Sustainability Standards, EU
              CSRD, CBAM, PCAF financed emissions, and climate disclosure
              regulations. Audit-defensible competency, not awareness training.
            </p>

            {/* Compliance badge pills */}
            <div className="mt-5 flex flex-wrap gap-3" data-id="element-1719">
              {['IFRS S1/S2', 'CSRD', 'PCAF', 'Basel IV', 'CBAM', 'TCFD'].map(badge => <span key={badge} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#9aa6af] bg-white text-sm font-medium text-carbon-deep" data-id="element-1720">

                    <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1721" />
                    {badge}
                  </span>)}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-1722">
              <a href="#programs" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#6bcb78] text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-1723">

                View Programs
                <ArrowRightIcon className="w-4 h-4" data-id="element-1724" />
              </a>
              <a href={MAILTO.trainingBrochure} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-[#2d3c48] text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-1725">

                <DownloadIcon className="w-4 h-4" data-id="element-1726" />
                Download Brochure
              </a>
            </div>
          </motion.div>

          {/* Right: Training image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative" data-id="element-1727">

            <div className="relative overflow-hidden rounded-2xl border border-[#9aa6af] shadow-xl shadow-carbon-deep/8" data-id="element-1728">
              <img src="/assets/external/ext-4c2f5449f9d2.jpg" alt="Institutional compliance training session" className="w-full h-[380px] lg:h-[440px] object-cover" data-id="element-1729" />

              <div className="absolute inset-0 bg-gradient-to-t from-carbon-deep/30 via-transparent to-transparent" data-id="element-1730" />
              {/* Stat overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between gap-3" data-id="element-1731">
                <div className="bg-white/95 border border-[#9aa6af] px-4 py-3 rounded-xl shadow-sm" data-id="element-1732">
                  <p className="text-xl font-bold text-carbon-bright" data-id="element-1733">$1,800</p>
                  <p className="text-xs text-carbon-deep mt-0.5" data-id="element-1734">
                    Per participant · All programs
                  </p>
                </div>
                <div className="bg-white/95 border border-[#9aa6af] px-4 py-3 rounded-xl shadow-sm" data-id="element-1735">
                  <p className="text-xl font-bold text-carbon-deep" data-id="element-1736">6</p>
                  <p className="text-xs text-carbon-deep mt-0.5" data-id="element-1737">
                    Certification programs
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-3 -right-3 w-24 h-24 rounded-2xl bg-carbon-bright/10 -z-10" aria-hidden="true" data-id="element-1738" />

          </motion.div>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   2. COURSE PROGRAMS GRID
════════════════════════════════════════ */
function ProgramsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
  const highDemandIds = new Set(['banking', 'foundations']);
  return <section id="programs" ref={ref} className="w-full bg-white py-20 sm:py-28 border-t border-carbon-geo" data-id="element-1739">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1740">
        <FadeIn className="text-center mb-14" data-id="element-1741">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-carbon-deep tracking-tight" data-id="element-1742">
            Six Structured Programs. One Compliance Standard.
          </h2>
          <p className="mt-4 text-lg text-carbon-deep max-w-2xl mx-auto" data-id="element-1743">
            Each program is built around a specific regulatory framework and
            industry context. All certifications are priced at{' '}
            <strong className="text-carbon-deep" data-id="element-1744">
              $1,800 USD per participant
            </strong>
            . Corporate cohort pricing available on request.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1745">
          {programs.map((program, i) => <motion.article key={program.id} className="bg-white rounded-2xl border border-[#9aa6af] flex flex-col overflow-hidden" initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.08 + i * 0.07,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1746">

              {/* Card header */}
              <div className="p-6 border-b border-carbon-geo" data-id="element-1747">
                <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-1748">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-carbon-deep" data-id="element-1749">
                    {program.category}
                  </p>
                  {highDemandIds.has(program.id) && <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange-700" data-id="element-1750">
                      <FlameIcon className="w-3 h-3" data-id="element-1751" />
                      In Demand
                    </span>}
                </div>
                <h3 className="text-base font-bold text-carbon-deep leading-snug" data-id="element-1752">
                  {program.title}
                </h3>
                <p className="mt-2 text-sm text-carbon-deep leading-relaxed" data-id="element-1753">
                  {program.description}
                </p>
              </div>

              {/* Card meta */}
              <div className="px-6 py-4 border-b border-carbon-geo grid grid-cols-3 gap-3" data-id="element-1754">
                <div data-id="element-1755">
                  <p className="text-[10px] text-carbon-deep/60 uppercase tracking-wider font-semibold" data-id="element-1756">
                    Duration
                  </p>
                  <p className="text-sm font-bold text-carbon-deep mt-0.5" data-id="element-1757">
                    {program.duration}
                  </p>
                </div>
                <div data-id="element-1758">
                  <p className="text-[10px] text-carbon-deep/60 uppercase tracking-wider font-semibold" data-id="element-1759">
                    CPD Hours
                  </p>
                  <p className="text-sm font-bold text-carbon-deep mt-0.5" data-id="element-1760">
                    {program.cpdHours}h
                  </p>
                </div>
                <div data-id="element-1761">
                  <p className="text-[10px] text-carbon-deep/60 uppercase tracking-wider font-semibold" data-id="element-1762">
                    Price
                  </p>
                  <p className="text-sm font-bold text-carbon-bright mt-0.5" data-id="element-1763">
                    ${program.price.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Expandable modules */}
              <div className="px-6 py-4 flex-1" data-id="element-1764">
                <button onClick={() => setSelectedProgram(selectedProgram === program.id ? null : program.id)} className="flex items-center justify-between w-full text-left" data-id="element-1765">

                  <span className="text-xs font-bold uppercase tracking-wider text-carbon-deep" data-id="element-1766">
                    {selectedProgram === program.id ? 'Hide' : 'View'} Modules (
                    {program.modules.length})
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 text-carbon-deep/50 transition-transform duration-200 ${selectedProgram === program.id ? 'rotate-180' : ''}`} data-id="element-1767" />

                </button>

                {selectedProgram === program.id && <motion.ul className="mt-3 space-y-2" initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} transition={{
              duration: 0.3
            }} data-id="element-1768">

                    {program.modules.map((mod, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-1769">
                        <CheckIcon className="w-3.5 h-3.5 text-carbon-bright mt-0.5 flex-shrink-0" data-id="element-1770" />
                        <span className="text-xs text-carbon-deep leading-snug" data-id="element-1771">
                          {mod.title}
                        </span>
                      </li>)}
                  </motion.ul>}
              </div>

              {/* CTA */}
              <div className="px-6 pb-6" data-id="element-1772">
                <button onClick={() => navigate(program.route)} className="flex items-center justify-center gap-2 w-full py-3 bg-black text-white text-sm font-semibold rounded-lg hover:bg-black/90 transition-colors shadow-sm shadow-black/20" data-id="element-1773">

                  View Program
                  <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1774" />
                </button>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   3. FEATURED COURSE — BANKING PROGRAM
════════════════════════════════════════ */
function FeaturedCourseSection() {
  const [activeTab, setActiveTab] = useState<'overview' | 'modules' | 'delivery'>('overview');
  const banking = programs.find(p => p.id === 'banking')!;
  const tabs = [{
    id: 'overview',
    label: 'Overview & Outcomes'
  }, {
    id: 'modules',
    label: 'Module Breakdown'
  }, {
    id: 'delivery',
    label: 'Delivery & Certification'
  }] as const;
  return <section className="w-full bg-white py-20 sm:py-28 border-t border-carbon-geo" data-id="element-1775">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1776">
        <FadeIn data-id="element-1777">
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest  bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1778">
            Featured Program
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-carbon-deep tracking-tight max-w-3xl" data-id="element-1779">
            Banking & Financial Institutions Program
          </h2>
          <p className="mt-4 text-lg text-carbon-deep max-w-2xl" data-id="element-1780">
            The most comprehensive climate certification for banks, DFIs, and
            investment firms operating under PCAF, IFRS S2, and Basel climate
            risk frameworks.
          </p>
        </FadeIn>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-1781">
          {/* Left: Tabbed content */}
          <div className="lg:col-span-2" data-id="element-1782">
            <div className="flex border-b border-carbon-geo mb-8" data-id="element-1783">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px ${activeTab === tab.id ? 'border-carbon-bright text-carbon-deep' : 'border-transparent text-carbon-deep hover:text-carbon-deep'}`} data-id="element-1784">

                  {tab.label}
                </button>)}
            </div>

            {activeTab === 'overview' && <motion.div key="overview" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-1785">

                <div className="mb-8" data-id="element-1786">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-deep/60 mb-4" data-id="element-1787">
                    Who This Is For
                  </h3>
                  <p className="text-base text-carbon-deep leading-relaxed" data-id="element-1788">
                    Commercial banks, development finance institutions,
                    investment managers, insurance companies, and credit unions.
                    Specifically designed for credit officers, risk managers,
                    sustainability teams, CFOs, and board members responsible
                    for climate-related financial disclosures.
                  </p>
                </div>

                <div className="mb-8" data-id="element-1789">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-deep/60 mb-4" data-id="element-1790">
                    Regulatory Alignment
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-1791">
                    {banking.regulatoryAlignment.map(r => <span key={r} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#9aa6af] bg-white text-sm font-medium text-carbon-deep" data-id="element-1792">

                        <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1793" />
                        {r}
                      </span>)}
                  </div>
                </div>

                <div className="mb-8" data-id="element-1794">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-deep/60 mb-4" data-id="element-1795">
                    Learning Outcomes
                  </h3>
                  <ul className="space-y-3" data-id="element-1796">
                    {['Calculate financed emissions using PCAF attribution methods across asset classes', 'Produce IFRS S1/S2 compliant climate disclosures for financial institutions', 'Integrate climate risk into credit underwriting and loan assessment processes', 'Conduct basic climate stress tests and transition risk scenario analysis', 'Identify and avoid greenwashing in green bond and sustainable finance products', 'Establish board-level climate governance and oversight frameworks'].map((outcome, i) => <li key={i} className="flex items-start gap-3" data-id="element-1797">
                        <div className="w-5 h-5 rounded-lg bg-carbon-bright/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1798">
                          <CheckIcon className="w-3 h-3 text-carbon-bright" strokeWidth={3} data-id="element-1799" />

                        </div>
                        <span className="text-sm text-carbon-deep leading-snug" data-id="element-1800">
                          {outcome}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-1801">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-deep/60 mb-4" data-id="element-1802">
                    Case Studies
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1803">
                    <div className="rounded-2xl border border-[#9aa6af] p-5 bg-white" data-id="element-1804">
                      <p className="text-sm font-bold text-carbon-deep mb-1" data-id="element-1805">
                        East African DFI
                      </p>
                      <p className="text-sm text-carbon-deep leading-snug" data-id="element-1806">
                        How a development finance institution built
                        PCAF-compliant financed emissions reporting for a $2.4B
                        loan portfolio.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-[#9aa6af] p-5 bg-white" data-id="element-1807">
                      <p className="text-sm font-bold text-carbon-deep mb-1" data-id="element-1808">
                        Commercial Bank Uganda
                      </p>
                      <p className="text-sm text-carbon-deep leading-snug" data-id="element-1809">
                        Integrating climate risk into credit assessment for
                        agricultural and energy sector lending under Basel
                        climate guidance.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>}

            {activeTab === 'modules' && <motion.div key="modules" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-1810">

                <div className="space-y-3" data-id="element-1811">
                  {[{
                num: '01',
                title: 'Financed Emissions & PCAF Methodology',
                desc: 'Introduction to the PCAF Standard, asset class coverage, data quality scoring, and attribution methods for loans, bonds, and equity.',
                duration: '4 hours'
              }, {
                num: '02',
                title: 'Portfolio Carbon Accounting & Attribution',
                desc: 'Practical calculation of portfolio-level emissions. Weighted average carbon intensity, absolute emissions, and sector benchmarking.',
                duration: '4 hours'
              }, {
                num: '03',
                title: 'Loan Book Emissions Calculation',
                desc: 'Applying PCAF to commercial real estate, corporate loans, project finance, and SME lending. Data gap management strategies.',
                duration: '4 hours'
              }, {
                num: '04',
                title: 'IFRS S1 & S2 for Financial Institutions',
                desc: 'Disclosure requirements, governance structures, strategy, risk management, and metrics under ISSB standards.',
                duration: '6 hours'
              }, {
                num: '05',
                title: 'Climate Risk in Credit Assessment',
                desc: 'Integrating physical and transition risk into credit underwriting, loan covenants, and sector exposure limits.',
                duration: '6 hours'
              }, {
                num: '06',
                title: 'Stress Testing & Transition Risk Modelling',
                desc: 'NGFS scenario analysis, orderly vs. disorderly transition scenarios, and basic stress test methodology.',
                duration: '4 hours'
              }, {
                num: '07',
                title: 'Sustainable Finance & Green Taxonomy',
                desc: 'EU Green Taxonomy alignment, green bond principles, sustainability-linked loans, and greenwashing risk management.',
                duration: '6 hours'
              }, {
                num: '08',
                title: 'Green Bonds & Avoiding Greenwashing',
                desc: 'ICMA Green Bond Principles, use of proceeds verification, impact reporting, and regulatory enforcement trends.',
                duration: '6 hours'
              }].map(mod => <div key={mod.num} className="flex gap-5 p-5 rounded-2xl border border-[#9aa6af] bg-white" data-id="element-1812">

                      <span className="text-2xl font-bold text-carbon-geo leading-none flex-shrink-0 w-8" data-id="element-1813">
                        {mod.num}
                      </span>
                      <div className="flex-1 min-w-0" data-id="element-1814">
                        <div className="flex items-start justify-between gap-3" data-id="element-1815">
                          <h4 className="text-sm font-bold text-carbon-deep" data-id="element-1816">
                            {mod.title}
                          </h4>
                          <span className="text-[10px] font-semibold text-carbon-deep/60 flex-shrink-0 flex items-center gap-1" data-id="element-1817">
                            <ClockIcon className="w-3 h-3" data-id="element-1818" />
                            {mod.duration}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-carbon-deep leading-relaxed" data-id="element-1819">
                          {mod.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>}

            {activeTab === 'delivery' && <motion.div key="delivery" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-1820">

                <div className="space-y-6" data-id="element-1821">
                  <div data-id="element-1822">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-deep/60 mb-4" data-id="element-1823">
                      Delivery Formats
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1824">
                      {[{
                    label: 'In-Person',
                    desc: 'Kampala, Uganda. Cohort-based, 5 consecutive days. Includes printed materials and networking sessions.'
                  }, {
                    label: 'Online',
                    desc: 'Self-paced with live instructor Q&A sessions. Access to recorded modules for 90 days post-enrollment.'
                  }, {
                    label: 'Hybrid',
                    desc: 'Blended format for corporate cohorts. 3 days in-person, 2 days online. Recommended for teams of 5+.'
                  }].map(format => <div key={format.label} className="rounded-2xl border border-[#9aa6af] p-5 bg-white" data-id="element-1825">

                          <p className="text-sm font-bold text-carbon-deep mb-2" data-id="element-1826">
                            {format.label}
                          </p>
                          <p className="text-xs text-carbon-deep leading-relaxed" data-id="element-1827">
                            {format.desc}
                          </p>
                        </div>)}
                    </div>
                  </div>

                  <div data-id="element-1828">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-carbon-deep/60 mb-4" data-id="element-1829">
                      Certification Details
                    </h3>
                    <div className="rounded-2xl border border-[#9aa6af] p-6 bg-white" data-id="element-1830">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1831">
                        <div data-id="element-1832">
                          <p className="text-xs font-bold text-carbon-deep/60 uppercase tracking-wider mb-2" data-id="element-1833">
                            Certificate Issued
                          </p>
                          <p className="text-sm text-carbon-deep font-semibold" data-id="element-1834">
                            CarbonSafe Certified Climate Finance Professional
                            (CCFP)
                          </p>
                        </div>
                        <div data-id="element-1835">
                          <p className="text-xs font-bold text-carbon-deep/60 uppercase tracking-wider mb-2" data-id="element-1836">
                            Assessment
                          </p>
                          <p className="text-sm text-carbon-deep" data-id="element-1837">
                            Written examination + practical case study. Minimum
                            70% pass mark.
                          </p>
                        </div>
                        <div data-id="element-1838">
                          <p className="text-xs font-bold text-carbon-deep/60 uppercase tracking-wider mb-2" data-id="element-1839">
                            Validity
                          </p>
                          <p className="text-sm text-carbon-deep" data-id="element-1840">
                            3 years. Annual CPD requirement of 8 hours for
                            renewal.
                          </p>
                        </div>
                        <div data-id="element-1841">
                          <p className="text-xs font-bold text-carbon-deep/60 uppercase tracking-wider mb-2" data-id="element-1842">
                            CPD Recognition
                          </p>
                          <p className="text-sm text-carbon-deep" data-id="element-1843">
                            40 CPD hours. Recognized for HR compliance
                            reporting.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </div>

          {/* Right: Sticky enrollment panel */}
          <div className="lg:sticky lg:top-24" data-id="element-1844">
            <div className="rounded-2xl border border-[#9aa6af] bg-white overflow-hidden shadow-lg shadow-carbon-deep/5" data-id="element-1845">
              <div className="bg-carbon-deep p-5 rounded-t-2xl" data-id="element-1846">
                <p className="text-xs font-bold uppercase tracking-widest text-carbon-bright mb-1" data-id="element-1847">
                  Banking & Finance Program
                </p>
                <p className="text-2xl font-bold text-white" data-id="element-1848">$1,800</p>
                <p className="text-xs text-white/50 mt-0.5" data-id="element-1849">per participant</p>
              </div>

              <div className="p-5 space-y-4 border-b border-carbon-geo" data-id="element-1850">
                <div className="flex items-center justify-between text-sm" data-id="element-1851">
                  <span className="text-carbon-deep flex items-center gap-2" data-id="element-1852">
                    <ClockIcon className="w-4 h-4" data-id="element-1853" /> Duration
                  </span>
                  <span className="font-bold text-carbon-deep" data-id="element-1854">5 days</span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-1855">
                  <span className="text-carbon-deep flex items-center gap-2" data-id="element-1856">
                    <GraduationCapIcon className="w-4 h-4" data-id="element-1857" /> Level
                  </span>
                  <span className="font-bold text-carbon-deep" data-id="element-1858">Advanced</span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-1859">
                  <span className="text-carbon-deep flex items-center gap-2" data-id="element-1860">
                    <AwardIcon className="w-4 h-4" data-id="element-1861" /> CPD Hours
                  </span>
                  <span className="font-bold text-carbon-deep" data-id="element-1862">40 hours</span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-1863">
                  <span className="text-carbon-deep flex items-center gap-2" data-id="element-1864">
                    <UsersIcon className="w-4 h-4" data-id="element-1865" /> Corporate Discount
                  </span>
                  <span className="font-bold text-carbon-bright" data-id="element-1866">5+ seats</span>
                </div>
              </div>

              <div className="p-5 space-y-3" data-id="element-1867">
                <a href={MAILTO.enrollCourse} className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#6bcb78] text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-sm shadow-carbon-bright/20" data-id="element-1868">

                  Enroll Now <ArrowRightIcon className="w-4 h-4" data-id="element-1869" />
                </a>
                <a href={MAILTO.courseSyllabus} className="flex items-center justify-center gap-2 w-full py-3 border-2 border-[#2d3c48] text-carbon-deep text-sm font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-1870">

                  <DownloadIcon className="w-4 h-4" data-id="element-1871" /> Download Syllabus
                </a>
                <a href={MAILTO.corporateCohort} className="flex items-center justify-center gap-2 w-full py-3 border border-[#9aa6af] text-carbon-deep text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1872">

                  Request Corporate Cohort
                </a>
              </div>

              <div className="px-5 pb-5" data-id="element-1873">
                <p className="text-[10px] text-carbon-deep/60 text-center leading-relaxed" data-id="element-1874">
                  Corporate cohort pricing available for 5+ participants.
                  Contact us for custom scheduling and on-site delivery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   4. TRUST & AUTHORITY
════════════════════════════════════════ */
function TrustSection() {
  const frameworks = ['GHG Protocol', 'IFRS S1/S2', 'PCAF Standard', 'EU CSRD / ESRS', 'CBAM', 'EUDR', 'Basel IV', 'TCFD', 'Verra VCS', 'Gold Standard', 'ISO 14064', 'UNFCCC Article 6'];
  const testimonials = [{
    quote: 'The Banking & Finance program gave our credit team the exact framework we needed to integrate climate risk into our loan assessment process. Audit-ready from day one.',
    author: 'Head of Risk, East African Commercial Bank',
    role: 'Corporate Enrollment, 12 participants'
  }, {
    quote: "CarbonSafe's certification is the only program I've found that actually addresses PCAF methodology with practical case studies. Not theory — real calculation exercises.",
    author: 'Chief Sustainability Officer, Development Finance Institution',
    role: 'Executive Enrollment'
  }, {
    quote: 'We enrolled our entire compliance team before our CSRD readiness audit. The training directly informed our double materiality assessment process.',
    author: 'Group Compliance Director, Manufacturing Conglomerate',
    role: 'Corporate Cohort, 8 participants'
  }];
  return <section className="w-full bg-white py-20 sm:py-28 border-t border-carbon-geo" data-id="element-1875">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1876">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-1877">
          <FadeIn data-id="element-1878">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest  bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1879">
              Regulatory Coverage
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight" data-id="element-1880">
              Every major framework.{' '}
              <span data-id="element-1881">
                One certification body.
              </span>
            </h2>
            <p className="mt-4 text-base text-carbon-deep leading-relaxed" data-id="element-1882">
              CarbonSafe certifications are aligned with the full spectrum of
              international and regional climate and sustainability reporting
              frameworks.
            </p>
            <div className="mt-8 flex flex-wrap gap-2" data-id="element-1883">
              {frameworks.map(f => <span key={f} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#9aa6af] bg-white text-sm font-medium text-carbon-deep" data-id="element-1884">

                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1885" />
                  {f}
                </span>)}
            </div>

            <div className="mt-10 grid grid-cols-3 gap-5 pt-8 border-t border-carbon-geo" data-id="element-1886">
              <div data-id="element-1887">
                <p className="text-4xl font-bold text-carbon-bright" data-id="element-1888">6</p>
                <p className="text-sm text-carbon-deep mt-1 font-medium" data-id="element-1889">
                  Certification programs
                </p>
              </div>
              <div data-id="element-1890">
                <p className="text-4xl font-bold text-carbon-bright" data-id="element-1891">12</p>
                <p className="text-sm text-carbon-deep mt-1 font-medium" data-id="element-1892">
                  Frameworks covered
                </p>
              </div>
              <div data-id="element-1893">
                <p className="text-4xl font-bold text-carbon-bright" data-id="element-1894">2026</p>
                <p className="text-sm text-carbon-deep mt-1 font-medium" data-id="element-1895">
                  First cohort launching
                </p>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-1896">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest  bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1897">
              Institutional Feedback
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight" data-id="element-1898">
              Trusted by compliance teams across East Africa
            </h2>
            <div className="mt-8 space-y-5" data-id="element-1899">
              {testimonials.map((t, i) => <div key={i} className="rounded-2xl border border-[#9aa6af] bg-white p-5" data-id="element-1900">

                  <p className="text-sm text-carbon-deep leading-relaxed italic" data-id="element-1901">
                    "{t.quote}"
                  </p>
                  <div className="mt-3 flex items-center gap-2" data-id="element-1902">
                    <span className="w-2 h-2 rounded-full bg-carbon-bright flex-shrink-0" data-id="element-1903" />
                    <div data-id="element-1904">
                      <p className="text-xs font-bold text-carbon-deep" data-id="element-1905">
                        {t.author}
                      </p>
                      <p className="text-[10px] text-carbon-deep/60 mt-0.5" data-id="element-1906">
                        {t.role}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   5. ENTERPRISE TRAINING (dark section — matches pattern)
════════════════════════════════════════ */
function EnterpriseSection() {
  const [form, setForm] = useState({
    institution: '',
    contact: '',
    email: '',
    program: '',
    participants: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const buildMailto = () => {
    const subject = encodeURIComponent('Enterprise Training Proposal Request');
    const body = encodeURIComponent(`Hello,\n\nI would like to request an enterprise training proposal for my institution.\n\nInstitution: ${form.institution}\nContact name & role: ${form.contact}\nEmail: ${form.email}\nProgram of interest: ${form.program}\nNumber of participants: ${form.participants}\n\nThank you`);
    return `mailto:operations@carbonsafe.earth?subject=${subject}&body=${body}`;
  };
  return <section className="w-full bg-carbon-deep py-20 sm:py-28" data-id="element-1907">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1908">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center" data-id="element-1909">
          <FadeIn data-id="element-1910">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1911">
              Enterprise ESG Training for Banks & Institutions
            </h2>
            <div className="w-12 h-0.5 bg-carbon-bright mt-5 mb-6" data-id="element-1912" />
            <p className="text-base text-white/60 leading-relaxed" data-id="element-1913">
              For institutions requiring structured, organisation-wide climate
              competency. CarbonSafe delivers custom cohort training with
              regulatory-aligned certification reporting for HR and compliance
              teams.
            </p>

            <div className="mt-8 space-y-5" data-id="element-1914">
              {[{
              icon: <UsersIcon className="w-5 h-5" data-id="element-1915" />,
              title: 'Custom Cohort Programs',
              desc: 'Dedicated training cohorts for your team. Minimum 5 participants. Custom scheduling, on-site or remote delivery.'
            }, {
              icon: <BriefcaseIcon className="w-5 h-5" data-id="element-1916" />,
              title: 'Board-Level Programs',
              desc: 'Executive certification designed for directors and C-suite. Condensed format, governance-focused, litigation risk coverage.'
            }, {
              icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-1917" />,
              title: 'Regulatory-Aligned Training',
              desc: "Programs mapped directly to your institution's regulatory obligations — PCAF, IFRS S2, Basel climate risk, CSRD."
            }, {
              icon: <FileTextIcon className="w-5 h-5" data-id="element-1918" />,
              title: 'Certification Reporting for HR/Compliance',
              desc: 'Structured completion records, CPD certificates, and audit-ready training logs for regulatory and HR compliance teams.'
            }].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-1919">
                  <div className="w-10 h-10 rounded-xl bg-carbon-bright/15 flex items-center justify-center flex-shrink-0" data-id="element-1920">
                    <div className="text-carbon-bright" data-id="element-1921">{item.icon}</div>
                  </div>
                  <div data-id="element-1922">
                    <p className="text-sm font-bold text-white" data-id="element-1923">{item.title}</p>
                    <p className="text-sm text-white/50 mt-1 leading-relaxed" data-id="element-1924">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-1925">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8" data-id="element-1926">
              <p className="text-xs font-bold uppercase tracking-widest text-carbon-bright mb-6" data-id="element-1927">
                Request Enterprise Proposal
              </p>
              <div className="space-y-4" data-id="element-1928">
                <div data-id="element-1929">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1930">
                    Institution Name
                  </label>
                  <input type="text" name="institution" value={form.institution} onChange={handleChange} placeholder="e.g. Stanbic Bank Uganda" className="w-full bg-white/5 border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1931" />

                </div>
                <div data-id="element-1932">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1933">
                    Contact Name & Role
                  </label>
                  <input type="text" name="contact" value={form.contact} onChange={handleChange} placeholder="e.g. Jane Doe, Head of Compliance" className="w-full bg-white/5 border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1934" />

                </div>
                <div data-id="element-1935">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1936">
                    Email Address
                  </label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="compliance@institution.com" className="w-full bg-white/5 border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1937" />

                </div>
                <div data-id="element-1938">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1939">
                    Program of Interest
                  </label>
                  <select name="program" value={form.program} onChange={handleChange} className="w-full bg-white/5 border border-white/15 text-white/70 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1940">

                    <option value="" className="bg-carbon-deep" data-id="element-1941">
                      Select a program
                    </option>
                    <option value="ESG Foundations" className="bg-carbon-deep" data-id="element-1942">
                      ESG Foundations
                    </option>
                    <option value="Banking & Financial Institutions" className="bg-carbon-deep" data-id="element-1943">

                      Banking & Financial Institutions
                    </option>
                    <option value="Oil & Gas Climate Compliance" className="bg-carbon-deep" data-id="element-1944">

                      Oil & Gas Climate Compliance
                    </option>
                    <option value="Manufacturing & Export" className="bg-carbon-deep" data-id="element-1945">

                      Manufacturing & Export
                    </option>
                    <option value="Agriculture & Forestry (AFOLU)" className="bg-carbon-deep" data-id="element-1946">

                      Agriculture & Forestry (AFOLU)
                    </option>
                    <option value="Executive & Board Certification" className="bg-carbon-deep" data-id="element-1947">

                      Executive & Board Certification
                    </option>
                    <option value="Custom Multi-Program" className="bg-carbon-deep" data-id="element-1948">

                      Custom Multi-Program
                    </option>
                  </select>
                </div>
                <div data-id="element-1949">
                  <label className="block text-xs font-semibold text-white/60 mb-1.5 uppercase tracking-wider" data-id="element-1950">
                    Number of Participants
                  </label>
                  <input type="number" name="participants" value={form.participants} onChange={handleChange} placeholder="e.g. 15" min="1" className="w-full bg-white/5 border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-carbon-bright transition-colors" data-id="element-1951" />

                </div>
                <a href={buildMailto()} className="flex items-center justify-center gap-2 w-full py-4 bg-[#6bcb78] text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-black/20 mt-2" data-id="element-1952">

                  Request Enterprise Proposal
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1953" />
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   6. RESOURCES
════════════════════════════════════════ */
function ResourcesSection() {
  return <section className="w-full bg-white py-16 sm:py-20 border-t border-carbon-geo" data-id="element-1954">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1955">
        <FadeIn className="mb-10" data-id="element-1956">
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest  bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1957">
            Resources
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight" data-id="element-1958">
            Institutional Resources
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-1959">
          <FadeIn delay={0.05} data-id="element-1960">
            <div className="rounded-2xl border border-[#9aa6af] p-6 bg-white h-full flex flex-col" data-id="element-1961">
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-4" data-id="element-1962">
                <FileTextIcon className="w-6 h-6 text-carbon-bright" data-id="element-1963" />
              </div>
              <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-1964">
                Whitepaper
              </h3>
              <p className="text-sm text-carbon-deep leading-relaxed flex-1" data-id="element-1965">
                "Climate Risk for African Banks" — A regulatory briefing on
                PCAF, Basel climate guidance, and IFRS S2 implementation for
                East African financial institutions.
              </p>
              <a href={MAILTO.trainingBrochure} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright hover:text-carbon-deep transition-colors" data-id="element-1966">

                <DownloadIcon className="w-4 h-4" data-id="element-1967" /> Download Whitepaper
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-1968">
            <div className="rounded-2xl border border-[#9aa6af] p-6 bg-white h-full flex flex-col" data-id="element-1969">
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-4" data-id="element-1970">
                <BookOpenIcon className="w-6 h-6 text-carbon-bright" data-id="element-1971" />
              </div>
              <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-1972">
                Course Brochure
              </h3>
              <p className="text-sm text-carbon-deep leading-relaxed flex-1" data-id="element-1973">
                Full program catalog with module breakdowns, regulatory
                alignment maps, pricing, and corporate enrollment terms for all
                six certification programs.
              </p>
              <a href={MAILTO.trainingBrochure} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright hover:text-carbon-deep transition-colors" data-id="element-1974">

                <DownloadIcon className="w-4 h-4" data-id="element-1975" /> Download Brochure
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-1976">
            <div className="rounded-2xl border border-[#9aa6af] p-6 bg-white h-full flex flex-col" data-id="element-1977">
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-4" data-id="element-1978">
                <AlertTriangleIcon className="w-6 h-6 text-carbon-bright" data-id="element-1979" />
              </div>
              <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-1980">
                Regulatory Brief
              </h3>
              <p className="text-sm text-carbon-deep leading-relaxed flex-1" data-id="element-1981">
                Subscribe to CarbonSafe's monthly regulatory compliance updates.
                CSRD, CBAM, PCAF, and Uganda Climate Change Act developments
                delivered to your inbox.
              </p>
              <a href={MAILTO.regulatoryUpdates} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright hover:text-carbon-deep transition-colors" data-id="element-1982">

                <MailIcon className="w-4 h-4" data-id="element-1983" /> Subscribe to Updates
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-1984">
            <div className="rounded-2xl border border-[#9aa6af] p-6 bg-white h-full flex flex-col" data-id="element-1985">
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-4" data-id="element-1986">
                <PhoneIcon className="w-6 h-6 text-carbon-bright" data-id="element-1987" />
              </div>
              <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-1988">
                Corporate Enrollment
              </h3>
              <p className="text-sm text-carbon-deep leading-relaxed flex-1" data-id="element-1989">
                Speak directly with our institutional training team. Custom
                cohort scheduling, volume pricing, and regulatory-aligned
                training plans for your institution.
              </p>
              <a href={MAILTO.trainingContact} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-carbon-bright hover:text-carbon-deep transition-colors" data-id="element-1990">

                <MailIcon className="w-4 h-4" data-id="element-1991" /> Contact Training Team
              </a>
            </div>
          </FadeIn>
        </div>

        {/* Bottom contact bar */}
        <div className="mt-10 rounded-2xl border border-[#9aa6af] bg-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1992">
          <div data-id="element-1993">
            <p className="text-sm font-bold text-carbon-deep" data-id="element-1994">
              Institutional Enrollment Enquiries
            </p>
            <p className="text-xs text-carbon-deep mt-0.5" data-id="element-1995">
              Regulatory references: IFRS S1/S2 · CSRD ESRS E1 · PCAF Standard
              v1.0 · Basel BCBS 530 · Uganda Climate Change Act 2021
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0" data-id="element-1996">
            <a href="tel:+256776007755" className="text-sm font-semibold text-carbon-deep hover:text-carbon-bright transition-colors flex items-center gap-1.5" data-id="element-1997">

              <PhoneIcon className="w-4 h-4" data-id="element-1998" /> +256 776 007755
            </a>
            <a href={MAILTO.trainingContact} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#6bcb78] text-white text-sm font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-sm shadow-carbon-bright/20" data-id="element-1999">

              <MailIcon className="w-4 h-4" data-id="element-2000" /> operations@carbonsafe.earth
            </a>
          </div>
        </div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   7. CTA — matches DMRVPageTemplate pattern
════════════════════════════════════════ */
function CTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full py-20 sm:py-28 bg-white" data-id="element-2001">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2002">
        <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
        background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
      }} initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2003">

          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #52B788, transparent)'
        }} aria-hidden="true" data-id="element-2004" />

          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #6BCB77, transparent)'
        }} aria-hidden="true" data-id="element-2005" />

          <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-2006">
            Build certified climate competency.
            <br data-id="element-2007" />
            <span data-id="element-2008">
              Enroll your institution today.
            </span>
          </h2>
          <p className="relative mt-5 text-lg text-white/70 max-w-xl mx-auto" data-id="element-2009">
            Join banks, oil & gas firms, and manufacturers across East Africa
            building audit-defensible ESG competency with CarbonSafe
            certification programs.
          </p>
          <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2010">
            <a href={MAILTO.corporateTraining} className="inline-flex items-center gap-2 px-8 py-4 bg-[#6bcb78] text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-2011">

              Request Corporate Training
              <ArrowRightIcon className="w-4 h-4" data-id="element-2012" />
            </a>
            <a href={MAILTO.trainingBrochure} className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-lg hover:bg-white/10 transition-colors" data-id="element-2013">

              Download Course Brochure
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ════════════════════════════════════════
   MAIN EXPORT
════════════════════════════════════════ */
export function ESGCourses() {
  return <>
      <HeroSection data-id="element-2014" />
      <ProgramsSection data-id="element-2015" />
      <FeaturedCourseSection data-id="element-2016" />
      <TrustSection data-id="element-2017" />
      <EnterpriseSection data-id="element-2018" />
      <ResourcesSection data-id="element-2019" />
      <CTASection data-id="element-2020" />
      <Footer data-id="element-2021" />
    </>;
}